import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  GridLegacy as Grid,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { faOpenid } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoginProps } from '..';
import { LocalLoginForm } from './local-login-form';

export const Main = styled('div')<{ $url: string }>`
  min-height: 100vh;
  width: 100%;
  display: flex;
  background: #f3f5f9;
`;

export const Shell = styled('div')`
  width: 100%;
  min-height: 100vh;
  display: flex;
  background: #f3f5f9;

  @media (max-width: 900px) {
    min-height: auto;
  }
`;

export const FormPanel = styled('div')`
  flex: 0 0 40%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing(4)};
  min-width: 0;
  background: transparent;
  width: 100%;

  @media (max-width: 900px) {
    flex: 1 1 100%;
    padding: ${(props) => props.theme.spacing(4)};
  }
`;

export const CoverPanel = styled('div')<{ $url: string }>`
  flex: 1 1 60%;
  background-image: url(${(props) => props.$url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  border-radius: 20px;
  margin: ${(props) => props.theme.spacing(4)};
  height: calc(100% - ${(props) => props.theme.spacing(8)});
  width: calc(100% - ${(props) => props.theme.spacing(8)});

  @media (max-width: 900px) {
    display: none;
  }
`;

export const StyledContainer = styled('div')<{ $nothing?: boolean }>`
  margin: ${(props) => props.theme.spacing(2, 0)};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const FormCard = styled(Paper)`
  width: min(460px, 92vw);
  border-radius: 16px;
  padding: ${(props) => props.theme.spacing(3, 4, 4)};
  box-shadow: none;
  background: transparent;
`;

export const CoverLogo = styled.img`
  position: absolute;
  left: ${(props) => props.theme.spacing(5)};
  bottom: ${(props) => props.theme.spacing(5)};
  width: 120px;
  height: auto;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.55;
`;

export const backgroundUrl = '/public/img/app_bg.jpg';
export const logoUrl = '/public/img/previsec_logo.png';

type LoginPageProps = Pick<
  LoginProps,
  'loginMutation' | 'oidcUrl' | 'oidcBtnLabel' | 'pathPrefix' | 'postLoginPath'
>;

const clearAuthRelatedLocalStorage = () => {
  [
    'autoLogout',
    'tokenInvalid',
    'lastRequestAt',
    'passwordResetSuccess',
  ].forEach((item) => localStorage.removeItem(item));
};

// eslint-disable-next-line max-lines-per-function
export const LoginPage: React.FC<LoginPageProps> = ({
  loginMutation,
  oidcUrl,
  oidcBtnLabel,
  pathPrefix,
  postLoginPath = '/',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [alerts] = useState(() => ({
    isAutoLogout: localStorage.getItem('autoLogout') === 'true',
    tokenInvalid: localStorage.getItem('tokenInvalid') === 'true',
    passwordResetSuccess:
      localStorage.getItem('passwordResetSuccess') === 'true',
  }));

  useEffect(() => {
    clearAuthRelatedLocalStorage();
  }, []);

  // This is the message that LocalLoginForm will display under the field
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  return (
    <>
      <Typography
        variant="subtitle1"
        paragraph
        align="center"
        sx={{ fontSize: '1.15rem', color: 'primary.main' }}
      >
        {String(t('login.formHeader'))}
      </Typography>

      {alerts.tokenInvalid && (
        <Alert
          severity="warning"
          style={{ width: '100%', marginBottom: '15px' }}
        >
          {String(t('login.errors.token'))}
        </Alert>
      )}
      {alerts.passwordResetSuccess && (
        <Alert
          severity="success"
          style={{ width: '100%', marginBottom: '15px' }}
        >
          {String(t('login.passwordReset.success'))}
        </Alert>
      )}
      {alerts.isAutoLogout && (
        <Alert severity="info" style={{ width: '100%', marginBottom: '15px' }}>
          {String(t('login.autoLogout'))}
        </Alert>
      )}

      <LocalLoginForm
        pathPrefix={pathPrefix}
        inlineErrorMessage={errorMessage}
        updateTokenFunction={async (token: string, refreshToken: string) => {
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          await new Promise((resolve) => setTimeout(resolve, 100));
          window.location.href = postLoginPath;
        }}
        submitFunction={async (email: string, password: string) => {
          try {
            setErrorMessage(null);

            const result = await loginMutation({
              variables: { email, password },
            });

            // Prefer GraphQL errors returned in payload (your network-tab "errors": [...])
            const errs = (result as any)?.errors;
            if (Array.isArray(errs) && errs.length > 0) {
              const msg =
                errs
                  .map((e: any) => e?.message ?? String(e))
                  .filter(Boolean)
                  .join(' | ') || String(t('login.errors.generic'));

              setErrorMessage(msg);

              // eslint-disable-next-line no-console
              console.error('result.errors:', errs);
            } else if ((result as any)?.apolloError) {
              // Fallback if you pass apolloError through from the app
              const apolloErr: any = (result as any).apolloError;
              const msg =
                apolloErr?.message ||
                apolloErr?.networkError?.message ||
                String(t('login.errors.generic'));
              setErrorMessage(msg);

              // eslint-disable-next-line no-console
              console.error('apolloError (passed through):', apolloErr);
            }

            return result;
          } catch (error: unknown) {
            const message =
              error instanceof Error
                ? error.message
                : String(t('login.errors.generic'));
            setErrorMessage(message || String(t('login.errors.generic')));
            throw error;
          }
        }}
      />

      {oidcUrl && (
        <Grid container style={{ paddingTop: '24px' }}>
          <Grid item>
            <Button
              variant="contained"
              startIcon={
                <FontAwesomeIcon
                  icon={faOpenid}
                  color={theme.palette.secondary.main}
                />
              }
              onClick={() => (window.location.pathname = oidcUrl)}
            >
              {String(t(oidcBtnLabel || 'login.oidc.submitButton'))}
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};
